import {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import lottie, { AnimationItem } from 'lottie-web';
import useBreakpoint from '../../hooks/useBreakpoint';
import { AnimationPlayerProps, AnimationSource } from './AnimationPlayer.types';

const AnimationPlayer = (
  {
    sizeX,
    sizeY,
    loop,
    autoPlay = true,
    webStyle,
    source,
    onComplete,
    onSourceLoaded,
  }: AnimationPlayerProps,
  ref: ForwardedRef<Partial<AnimationItem>>,
) => {
  const breakpoint = useBreakpoint();
  const [animation, setAnimation] = useState<AnimationSource>();
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  const loadSource = useCallback(async () => {
    const result = await (typeof source === 'function'
      ? source()
      : (await source[breakpoint])());

    return 'default' in result ? result.default : result;
  }, [source, breakpoint]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const handleComplete = () => onComplete?.(false);

    let animationItem: AnimationItem;

    loadSource().then((animation) => {
      // Destroy existing animation if it exists
      if (animationRef.current) {
        animationRef.current.destroy();
      }

      // Load new animation
      animationItem = lottie.loadAnimation({
        container,
        loop,
        autoplay: autoPlay,
        animationData: animation,
      });

      animationRef.current = animationItem;

      setAnimation(animation as never);

      onSourceLoaded?.();

      if (onComplete) {
        animationItem.addEventListener('complete', handleComplete);
      }
    });

    return () => {
      if (animationItem) {
        animationItem.removeEventListener('complete', handleComplete);
        animationItem.destroy();
      }
    };
  }, [autoPlay, loop, loadSource, onComplete, onSourceLoaded]);

  useImperativeHandle(ref, () => {
    if (!animationRef.current || !animation) {
      return {};
    }

    return animationRef.current;
  }, [animation]);

  return (
    <div
      ref={containerRef}
      style={{ width: sizeX, height: sizeY, ...webStyle }}
    />
  );
};

export default memo(forwardRef(AnimationPlayer));
